import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems, marketplaceCurrency) => {
  const amount = lineItems.reduce((total, item) => {
    return total.plus(item.lineTotal.amount);
  }, new Decimal(0));
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  return new Money(amount, currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
};

/**
 * Returns non-commission, reversal line items visible to the buyer.
 *
 * The refund figure is always the buyer's refund, so it must be sourced from the
 * customer-visible line items. The provider view filters `lineItems` to
 * provider-included ones, which drops the customer-only sales-tax reversal; using
 * the buyer-visible set keeps the refund total (item + sales tax) correct in both
 * the customer and provider views.
 */
const nonCommissionBuyerReversalLineItems = lineItems => {
  return lineItems.filter(
    item => !isCommission(item) && item.reversal && item.includeFor?.includes('customer')
  );
};

/**
 * A component that renders the refund as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - Role-filtered line items (customer view fallback)
 * @param {Array<propTypes.lineItem>} props.allLineItems - Unfiltered line items, so the provider view still sees the customer-only sales-tax reversal
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemRefundMaybe = props => {
  const { lineItems, allLineItems, intl, marketplaceCurrency } = props;

  // Buyer-visible non-commission reversals, from the unfiltered set when available.
  const refundLineItems = nonCommissionBuyerReversalLineItems(allLineItems || lineItems);

  const refund = lineItemsTotal(refundLineItems, marketplaceCurrency);

  const formattedRefund = refundLineItems.length > 0 ? formatMoney(intl, refund) : null;

  return formattedRefund ? (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="OrderBreakdown.refund" />
      </span>
      <span className={css.itemValue}>{formattedRefund}</span>
    </div>
  ) : null;
};

export default LineItemRefundMaybe;
